import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { useAuth } from '../context/AuthContext';

export function ConductWarningModal({ isOpen, onClose }) {
  const { api, refreshUser } = useAuth();
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setSubmitting(true);
    if (dontShowAgain) {
      try {
        await api.post('/messages/dismiss-conduct-warning');
        if (refreshUser) await refreshUser();
      } catch (e) {
        // Non-blocking — still close the modal
      }
    }
    setSubmitting(false);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      data-testid="conduct-warning-modal"
    >
      <div className="bg-[var(--theme-card)] rounded-2xl w-full max-w-md border border-[var(--theme-border)] shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-[var(--theme-border)]">
          <div className="w-10 h-10 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-[#1E3A5F]" />
          </div>
          <h2 className="text-lg font-bold text-[var(--theme-text-primary)]">Before you message</h2>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          <p className="text-sm text-[var(--theme-text-primary)] leading-relaxed">
            WANTD uses an automated system to suspend and ban accounts for misconduct.
            This system is driven by community reports and there is <strong>no appeal process</strong>.
          </p>

          <div className="bg-[var(--theme-hover)] rounded-xl p-4 space-y-2">
            <p className="text-sm font-semibold text-[var(--theme-text-primary)]">To keep your account in good standing:</p>
            <ul className="text-sm text-[var(--theme-text-secondary)] space-y-1.5 list-disc list-inside">
              <li>Be respectful and act in good faith</li>
              <li>Never send phishing links or scam messages</li>
              <li>Follow WANTD's community guidelines</li>
            </ul>
          </div>

          <p className="text-xs text-[var(--theme-text-muted)]">
            See our{' '}
            <Link to="/terms" className="text-[#C9956C] hover:underline" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/community-guidelines" className="text-[#C9956C] hover:underline" target="_blank" rel="noopener noreferrer">
              Community Guidelines
            </Link>{' '}
            for full details.
          </p>

          <label className="flex items-center gap-2 pt-1 cursor-pointer select-none">
            <Checkbox
              checked={dontShowAgain}
              onCheckedChange={(v) => setDontShowAgain(!!v)}
              className="h-5 w-5 rounded border-2 border-[var(--theme-border-hover)] data-[state=checked]:bg-[#1E3A5F] data-[state=checked]:border-[#1E3A5F] data-[state=checked]:text-white"
              data-testid="conduct-dont-show-again"
            />
            <span className="text-sm text-[var(--theme-text-primary)]">Don't show this again</span>
          </label>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[var(--theme-border)]">
          <Button
            onClick={handleConfirm}
            disabled={submitting}
            className="w-full bg-[#1E3A5F] hover:bg-[#1E3A5F]/90 text-white"
            data-testid="conduct-warning-confirm"
          >
            I understand
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ConductWarningModal;
